import { apiFetch } from './client';
import { tokenStore } from './tokens';
import type { AuthTokens } from '@/types/api';

export interface RegisterInput {
  readonly email: string;
  readonly password: string;
  readonly name: string;
}

export interface LoginInput {
  readonly email: string;
  readonly password: string;
}

async function login(input: LoginInput): Promise<AuthTokens> {
  const tokens = await apiFetch<AuthTokens>('/auth/login', {
    method: 'POST',
    body: input,
    skipAuth: true,
  });
  tokenStore.set({
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: tokens.expiresAt,
  });
  return tokens;
}

async function register(input: RegisterInput): Promise<AuthTokens> {
  const tokens = await apiFetch<AuthTokens>('/auth/register', {
    method: 'POST',
    body: input,
    skipAuth: true,
  });
  tokenStore.set({
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: tokens.expiresAt,
  });
  return tokens;
}

async function logout(): Promise<void> {
  const refreshToken = tokenStore.getRefreshToken();
  try {
    if (refreshToken) {
      await apiFetch<void>('/auth/logout', {
        method: 'POST',
        body: { refreshToken },
        skipAuth: true,
        skipRefresh: true,
      });
    }
  } finally {
    tokenStore.clear();
  }
}

async function refreshTokens(): Promise<AuthTokens | null> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return null;
  const tokens = await apiFetch<AuthTokens>('/auth/refresh', {
    method: 'POST',
    body: { refreshToken },
    skipAuth: true,
    skipRefresh: true,
  });
  tokenStore.set({
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: tokens.expiresAt,
  });
  return tokens;
}

export const authApi = {
  login,
  register,
  logout,
  refreshTokens,

  requestEmailVerification(): Promise<void> {
    return apiFetch<void>('/auth/email-verification/request', { method: 'POST' });
  },

  confirmEmailVerification(token: string): Promise<void> {
    return apiFetch<void>('/auth/email-verification/confirm', {
      method: 'POST',
      body: { token },
      skipAuth: true,
    });
  },

  confirmEmailChange(token: string): Promise<void> {
    return apiFetch<void>('/auth/email-change/confirm', {
      method: 'POST',
      body: { token },
    });
  },

  forgotPassword(email: string): Promise<void> {
    return apiFetch<void>('/auth/password/forgot', {
      method: 'POST',
      body: { email },
      skipAuth: true,
    });
  },

  resetPassword(input: { token: string; password: string }): Promise<void> {
    return apiFetch<void>('/auth/password/reset', {
      method: 'POST',
      body: input,
      skipAuth: true,
    });
  },
};
